import React from 'react';
import { Box, Grid, Typography, Chip, Button } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import HotelIcon from '@mui/icons-material/Hotel';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';
import PaymentsIcon from '@mui/icons-material/Payments';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import FlightIcon from '@mui/icons-material/Flight';
import StarIcon from '@mui/icons-material/Star';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import colors from '../../../../config/colors';
import { useSelector } from 'react-redux';

const ULTRA_PDF_URL = 'https://www.arssenasa.gob.do/wp-content/uploads/2026/08/Brochure_Digital_Ultra.pdf';

const beneficiosUltra = [
  { icon: <HealthAndSafetyIcon sx={{ fontSize: 24, color: colors.ultraAccentLight }} />, label: 'Cobertura ampliada del PDSS', sub: 'Más de 800 coberturas adicionales al PDSS.' },
  { icon: <HotelIcon sx={{ fontSize: 24, color: colors.ultraAccentLight }} />, label: 'Hospitalización al 100%', sub: 'Al 100% sin límite por año.' },
  { icon: <SecurityIcon sx={{ fontSize: 24, color: colors.ultraAccentLight }} />, label: 'Protección 4M alto costo', sub: 'Hasta RD$4,000,000 para alto costo.' },
  { icon: <GroupIcon sx={{ fontSize: 24, color: colors.ultraAccentLight }} />, label: 'Prestadores ULTRA', sub: 'Red Ultra.' },
  { icon: <PaymentsIcon sx={{ fontSize: 24, color: colors.ultraAccentLight }} />, label: 'Reembolso ágil', sub: 'Hasta 90% fuera de la red.' },
  { icon: <RoomServiceIcon sx={{ fontSize: 24, color: colors.ultraAccentLight }} />, label: 'Atención Concierge', sub: 'Servicio Concierge.' },
  { icon: <TrendingUpIcon sx={{ fontSize: 24, color: colors.ultraAccentLight }} />, label: 'Excelente valor', sub: 'Excelente relación cobertura-precio.' },
];

const beneficiosComplementarios = [
  { icon: <AirportShuttleIcon sx={{ fontSize: 18, color: colors.ultraAccentLight }} />, label: 'Ambulancia aérea y terrestre' },
  { icon: <FlightIcon sx={{ fontSize: 18, color: colors.ultraAccentLight }} />, label: 'Plan de repatriación' },
  { icon: <StarIcon sx={{ fontSize: 18, color: colors.ultraAccentLight }} />, label: 'Últimos gastos' },
  { icon: <LocalHospitalIcon sx={{ fontSize: 18, color: colors.ultraAccentLight }} />, label: 'Plan odontológico' },
  { icon: <AddCircleOutlineIcon sx={{ fontSize: 18, color: colors.ultraAccentLight }} />, label: 'Entre otros' },
];

const UltraBanner = ({ planes = [] }) => {
  const isUltraRedux = useSelector((state) => state.afiliado.infoAfiliado.isUltra);
  const ultraDarkMode = useSelector((state) => state.afiliado.infoAfiliado.ultraDarkMode);
  const isDark = isUltraRedux && ultraDarkMode;
  const isUltraLight = isUltraRedux && !ultraDarkMode;

  const esUltraOVip = (plan) => {
    if (!plan) return false;
    if (plan.esVip === true || plan.isVip === true || plan.vip === true) return true;
    if (plan.esUltra === true || plan.isUltra === true || plan.ultra === true) return true;
    const nombre = (plan.planes_Nombre ?? plan.nombre ?? plan.nombre_plan ?? plan.plan ?? plan.descripcion ?? '').toLowerCase();
    return nombre.includes('ultra');
  };

  const planUltra = planes.find(esUltraOVip);

  // Mostrar banner si encontramos el plan en el array O si Redux ya sabe que es Ultra
  if (!planUltra && !isUltraRedux) return null;

  return (
    <Box sx={{
      my: 3,
      borderRadius: '18px',
      overflow: 'hidden',
      background: `linear-gradient(160deg, ${colors.ultraBlack} 0%, ${colors.ultraMid} 100%)`,
      boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
      position: 'relative',
    }}>
      {/* Top accent line in sage color */}
      <Box sx={{
        height: 2,
        background: `linear-gradient(90deg, transparent, ${colors.ultraAccent}, transparent)`,
      }} />

      <Grid container alignItems="stretch">
        {/* Left: identity */}
        <Grid item xs={12} md={4} sx={{
          p: { xs: 3, md: 4 },
          display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.5,
          borderRight: { md: `1px solid ${colors.ultraAccentBorder}` },
          borderBottom: { xs: `1px solid ${colors.ultraAccentBorder}`, md: 'none' },
        }}>
          <Box sx={{ mb: 1 }}>
            <img
              src={colors.ultraLogoUrl}
              alt="SeNaSa Ultra"
              style={{ height: 40, width: 'auto' }}
              onError={(e) => { e.target.src = '/Ultra_PNG/SeNaSa_Ultra_Logo-04.png'; }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="ACTIVO" size="small" sx={{
              background: colors.ultraAccentMuted,
              color: colors.ultraAccentLight,
              fontWeight: 700, fontSize: 9, height: 18, letterSpacing: 1,
              border: `1px solid ${colors.ultraAccentBorder}`,
            }} />
          </Box>

          <Typography sx={{
            color: colors.ultraWhite,
            fontSize: { xs: 26, md: 30 },
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: -0.5,
          }}>
            Ultra
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, lineHeight: 1.6 }}>
            Experiencia de salud elevada con coberturas pensadas para llevar tu salud al siguiente nivel.
          </Typography>
          <Button
            variant="contained"
            disableRipple
            disableElevation
            onClick={() => window.open(ULTRA_PDF_URL, '_blank', 'noopener,noreferrer')}
            style={{ backgroundColor: isDark ? colors.ultraAccent : colors.ulCardDark, color: 'white', fontFamily: 'Montserrat', fontWeight: 'bold', textTransform: 'none', height: 48, borderRadius: 8, fontSize: 15, marginTop: 16, alignSelf: 'flex-start', boxShadow: 'none', paddingLeft: 28, paddingRight: 28, letterSpacing: 0.3 }}
          >
            Explorar Ultra
          </Button>
        </Grid>

        {/* Center: beneficios */}
        <Grid item xs={12} md={4} sx={{
          p: { xs: 3, md: 4 },
          borderRight: { md: `1px solid ${colors.ultraAccentBorder}` },
          borderBottom: { xs: `1px solid ${colors.ultraAccentBorder}`, md: 'none' },
        }}>
          <Typography sx={{
            color: colors.ultraAccentLight,
            fontWeight: 700, fontSize: 10,
            letterSpacing: 2, textTransform: 'uppercase', mb: 2,
          }}>
            Beneficios
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {beneficiosUltra.map((b, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.2,
                p: 1.2, borderRadius: '10px',
                background: colors.ultraAccentMuted,
                border: `1px solid ${colors.ultraAccentBorder}`,
              }}>
                <Box sx={{ mt: 0.2, flexShrink: 0 }}>{b.icon}</Box>
                <Box>
                  <Typography sx={{ color: colors.ultraWhite, fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>
                    {b.label}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, lineHeight: 1.3 }}>
                    {b.sub}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Right: Beneficios complementarios */}
        <Grid item xs={12} md={4} sx={{ p: { xs: 3, md: 4 } }}>
          <Typography sx={{
            color: colors.ultraAccentLight,
            fontWeight: 700, fontSize: 10,
            letterSpacing: 2, textTransform: 'uppercase', mb: 2,
          }}>
            Beneficios complementarios
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {beneficiosComplementarios.map((s, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 1.2,
                p: 1, borderRadius: '8px',
                background: colors.ultraAccentMuted,
                border: `1px solid ${colors.ultraAccentBorder}`,
              }}>
                {s.icon}
                <Typography sx={{ color: colors.ultraWhite, fontSize: 12, fontWeight: 500 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UltraBanner;
